import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { makeTheme } from 'theme';
import { useMode } from 'stores/themeStore';

/**
 * Mirrors the active mode onto <html> so the CSS custom properties in
 * index.css can switch with it, and tells the browser which scrollbars and
 * form controls to draw.
 */
function useDocumentMode(mode) {
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;
  }, [mode]);
}

/** The app-wide provider: whatever the user picked, resolved and applied. */
export default function ThemeModeProvider({ children }) {
  const { i18n } = useTranslation();
  const mode = useMode();
  const language = i18n.resolvedLanguage;
  const theme = useMemo(() => makeTheme(mode, language), [mode, language]);
  useDocumentMode(mode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

/**
 * Pins a subtree to the light theme regardless of preference: the landing page
 * is light-locked Tailwind and deliberately out of scope for dark mode, so it
 * gets the light theme rather than a half-darkened one.
 */
export function ForceLightTheme({ children }) {
  const { i18n } = useTranslation();
  const appMode = useMode();
  const language = i18n.resolvedLanguage;
  const theme = useMemo(() => makeTheme('light', language), [language]);

  // Restore the app's own mode on the way out: ThemeModeProvider's effect is
  // keyed on `mode` and will not re-run if the preference is unchanged.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', 'light');
    root.style.colorScheme = 'light';
    return () => {
      root.setAttribute('data-theme', appMode);
      root.style.colorScheme = appMode;
    };
  }, [appMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
